/**
 * Shared markdown utilities used by CommonMarkdown and URL extraction
 * Extracted from CommonMarkdown.tsx to ensure consistency
 */

import { DOMAIN } from '../config';
import { documentSlug } from './documentMetadata';

interface ParsedFileIdHref {
  fileId: number;
  fragment: string;
}

export function parseFileIdHref(href: string): ParsedFileIdHref | null {
  const match = href.match(/^file_id:(\d+)(#.*)?$/i);
  if (!match?.[1]) {
    return null;
  }

  return {
    fileId: Number.parseInt(match[1], 10),
    fragment: match[2] ?? '',
  };
}

interface ParsedStorageNodeHref {
  nodeId: number;
  fragment: string;
}

/**
 * Parse a `storage_node:<id>` reference — the client-file citation scheme.
 * Mirrors parseFileIdHref, but resolves against `storage_nodes`, a distinct
 * table/id-space from `user_files` (see file_id:<id> above).
 */
export function parseStorageNodeHref(
  href: string
): ParsedStorageNodeHref | null {
  const match = href.match(/^storage_node:(\d+)(#.*)?$/i);
  if (!match?.[1]) {
    return null;
  }

  return {
    nodeId: Number.parseInt(match[1], 10),
    fragment: match[2] ?? '',
  };
}

/**
 * Helper function to get the hostname from DOMAIN config
 */
function getDomainHostname(): string {
  try {
    const domainUrl = new URL(DOMAIN);
    return domainUrl.hostname;
  } catch {
    // Fallback to www.lexomat.sk if DOMAIN is not a valid URL
    return 'www.lexomat.sk';
  }
}

/**
 * URL transformation rules for converting external URLs to configured domain
 */
const URL_TRANSFORMATION_RULES = {
  'slov-lex.sk': {
    pathTransformations: {
      '/ezbierky/pravne-predpisy/': '/sk/dokument/',
      '/pravne-predpisy/': '/sk/dokument/',
    },
    get targetDomain() {
      return getDomainHostname();
    },
  },
} as const;

/**
 * Synchronous version of URL transformation for immediate rendering
 * @param url - The full URL to potentially transform
 * @returns Transformed URL or original URL if no transformation applies
 */
function transformExternalUrlSync(url: string): string {
  try {
    const urlObj = new URL(url);

    // Check each domain in our transformation rules
    for (const [sourceDomain, rules] of Object.entries(
      URL_TRANSFORMATION_RULES
    )) {
      // Match domain (with or without www, case sensitive)
      const domainPattern = new RegExp(
        `^(www\\.)?${sourceDomain.replace('.', '\\.')}$`
      );
      if (!domainPattern.test(urlObj.hostname)) {
        continue;
      }

      // Check for path transformations
      for (const [sourcePath, targetPath] of Object.entries(
        rules.pathTransformations
      )) {
        if (urlObj.pathname.startsWith(sourcePath)) {
          // Transform the URL
          const newPathname = urlObj.pathname.replace(sourcePath, targetPath);

          // Use URL constructor to properly handle query params and fragments
          const baseUrl = DOMAIN.endsWith('/') ? DOMAIN.slice(0, -1) : DOMAIN;
          const transformedUrlObj = new URL(`${baseUrl}${newPathname}`);

          // Copy search params and hash from original URL
          transformedUrlObj.search = urlObj.search;
          transformedUrlObj.hash = urlObj.hash;

          return transformedUrlObj.toString();
        }
      }

      // If domain matches but no path transformation applies, log warning
      console.warn(
        `[URL Transform] Unmatched ${sourceDomain} URL pattern: ${url}`
      );
      return url;
    }

    // External URLs from unknown hosts should pass through unchanged
    // Only slov-lex.sk URLs (handled above) get transformed to internal paths
    // This preserves web search result URLs from external domains
    return url;
  } catch {
    // Invalid URL, return as-is
    return url;
  }
}

/**
 * Synchronous version of Linkformat for immediate rendering
 */
export function Linkformat(
  href: string,
  locale = 'sk',
  options?: { isIframe?: boolean }
): string {
  // Return empty string or original href for invalid inputs
  if (!href || typeof href !== 'string') {
    return href || '';
  }

  // Handle canonical user file references.
  // These are resolved lazily on click/preview via user-files API.
  if (parseFileIdHref(href)) {
    return href;
  }

  // Handle client-file (storage_node) references — resolved lazily on
  // click/preview via storageService.getNode, mirroring file_id: above.
  if (parseStorageNodeHref(href)) {
    return href;
  }

  // Guard: leaked backend short codes (=digits or =digits#fragment)
  // These should never reach the frontend; if they do, return empty to avoid broken links
  if (/^=\d+(?:#.+)?$/.test(href)) {
    return '';
  }

  // Handle numeric document_content_id (pure numeric)
  if (/^\d+$/.test(href)) {
    const baseDomain = DOMAIN.endsWith('/') ? DOMAIN.slice(0, -1) : DOMAIN;
    const url = `${baseDomain}/${locale}/${documentSlug(locale)}/${href}`;

    if (options?.isIframe) {
      try {
        const urlObj = new URL(url);
        urlObj.searchParams.set('preview', 'true');
        return urlObj.toString();
      } catch {
        return url;
      }
    }

    return url;
  }

  // Handle numeric document_content_id with fragment (e.g., "12345#section-1")
  const numericWithFragmentMatch = href.match(/^(\d+)(#.+)$/);
  if (numericWithFragmentMatch) {
    const [, id, fragment] = numericWithFragmentMatch;
    const baseDomain = DOMAIN.endsWith('/') ? DOMAIN.slice(0, -1) : DOMAIN;
    const url = `${baseDomain}/${locale}/${documentSlug(locale)}/${id}${fragment}`;

    if (options?.isIframe) {
      try {
        const urlObj = new URL(url);
        urlObj.searchParams.set('preview', 'true');
        return urlObj.toString();
      } catch {
        return url;
      }
    }

    return url;
  }

  // Check if this is a full URL (http/https) and apply transformations if applicable
  if (href.match(/^https?:\/\//)) {
    const transformedUrl = transformExternalUrlSync(href);
    const isIframe = options?.isIframe;

    // Add preview parameter for iframe usage
    if (isIframe) {
      try {
        const url = new URL(transformedUrl);
        url.searchParams.set('preview', 'true');
        return url.toString();
      } catch {
        return transformedUrl;
      }
    }

    return transformedUrl;
  }

  // Normalize prefixes: /SK, //SK, ///SK, SK, or sk to /SK
  const normalizedHref = href.replace(/^([\/]{0,3}SK|sk)/i, '/SK');

  // Construct full URL based on normalized href
  if (normalizedHref.startsWith('/SK')) {
    const baseDomain = DOMAIN.endsWith('/') ? DOMAIN.slice(0, -1) : DOMAIN;
    const baseUrl = `${baseDomain}/${locale}/${documentSlug(locale)}${normalizedHref}`;

    // Add preview parameter for iframe usage
    if (options?.isIframe) {
      try {
        const url = new URL(baseUrl);
        url.searchParams.set('preview', 'true');
        return url.toString();
      } catch {
        return baseUrl;
      }
    }

    return baseUrl;
  } else if (normalizedHref.startsWith('/pdf')) {
    return `https://static.slov-lex.sk${normalizedHref}`;
  }

  // Return normalized href for other cases
  return normalizedHref;
}

/**
 * Extract a meaningful short label from a URL or document reference.
 * Used as anchor text when the LLM produces bare URL references
 * instead of proper markdown links with descriptive text.
 */
export function extractLabelFromUrl(url: string): string {
  if (/^https?:\/\//i.test(url)) {
    try {
      const parsed = new URL(url);
      const hostname = parsed.hostname.replace(/^www\./, '');
      return hostname.length > 30 ? `${hostname.slice(0, 27)}...` : hostname;
    } catch {
      return 'link';
    }
  }

  if (/^(\/+)?SK/i.test(url)) {
    const cleaned = url.replace(/^\/+/, '');
    return cleaned.length > 25 ? `${cleaned.slice(0, 22)}...` : cleaned;
  }

  const numMatch = url.match(/^(\d+)(?:#.*)?$/);
  if (numMatch) {
    return `dok. ${numMatch[1]}`;
  }

  return 'link';
}

/**
 * Preprocess markdown content before parsing
 * This normalizes various URL patterns for consistent extraction
 *
 * NOTE: Validation is now done explicitly when streaming completes (in ResearchSession.tsx)
 * rather than on every render to avoid validating:
 * - Historical messages (already validated)
 * - Partial streaming content (incomplete)
 */
export function preprocessMarkdown(content: string): string {
  // Validation removed from here - now done explicitly when streaming completes

  // Step 0a: Strip extraction relevance labels ([JADRO], [KONTEXT], [OKRAJOVÉ])
  // These are internal tags from the extraction stage that must never reach the UI.
  // Backend post-processing strips them, but this is a safety net for streaming chunks.
  content = content.replace(/\[(?:JADRO|KONTEXT|OKRAJOV[ÉE])\]\s*/g, '');

  // Step 0b: Strip bare short codes not inside markdown links: text(=3) or text(=5#7)
  // Only matches (=N...) NOT preceded by ] (which would be a valid markdown link).
  content = content.replace(/(?<!\])\(=\d+(?:#[^\s)]+)?\)/g, '');

  // Step 1: Handle Slovak legal references with backticks
  // Pattern: [`[text](/SK/path)`] → [text](/SK/path)
  content = content.replace(
    /\[`(\[[^\]]+\]\([^)]+\))`\]/g,
    (_match, innerLink) => innerLink
  );

  // Step 2: Handle double-wrapped markdown links
  // Pattern: [[text](url)] → [text](url)
  content = content.replace(
    /\[(\[[^\]]+\]\([^)]+\))\]/g,
    (_match, innerLink) => innerLink
  );

  // Step 3: Handle bare SK links and HTTP links
  // Pattern: [/SK/path] or [https://url] → [label](/SK/path) or [label](https://url)
  content = content.replace(
    /\[((?:\/+)?SK[^\]\s]*|https?:\/\/[^\]\s]+)\]/g,
    (_match, url) => {
      // Escape underscores in URL to prevent markdown emphasis interpretation
      // Use negative lookbehind to avoid double-escaping already-escaped underscores
      const escapedUrl = url.replace(/(?<!\\)_/g, '\\_');
      return `[${extractLabelFromUrl(url)}](${escapedUrl})`;
    }
  );

  // Step 3.5: Handle numeric document IDs with optional fragments
  // Pattern: [12345] or [12345#section.name_with_underscores] → [dok. 12345](12345) or [dok. 12345](12345#section...)
  // This handles LLM output like [4515706#prilohy.priloha-1.op-legislativne_pravidla...]
  // IMPORTANT: Use negative lookahead (?!\() to NOT match if followed by ( - that's already a valid markdown link
  // e.g., [14](4791016) should NOT match [14] because it's part of a valid link
  //
  // First, handle parenthesized bare IDs: ([4771135]) → [dok. 4771135](4771135)
  // This pattern appears when LLM wraps document references in parentheses
  content = content.replace(/\(\[(\d+(?:#[^\]]+)?)\]\)/g, (_match, url) => {
    const cleanUrl = url.replace(/ .*$/, '');
    const escapedUrl = cleanUrl.replace(/(?<!\\)_/g, '\\_');
    return `[${extractLabelFromUrl(cleanUrl)}](${escapedUrl})`;
  });
  // Then handle regular bare IDs
  content = content.replace(/\[(\d+(?:#[^\]]+)?)\](?!\()/g, (_match, url) => {
    // Escape underscores in URL to prevent markdown emphasis interpretation
    // Use negative lookbehind to avoid double-escaping already-escaped underscores
    const cleanUrl = url.replace(/ .*$/, '');
    const escapedUrl = cleanUrl.replace(/(?<!\\)_/g, '\\_');
    return `[${extractLabelFromUrl(cleanUrl)}](${escapedUrl})`;
  });

  // Step 3.6: Split malformed multi-URL markdown links
  // Pattern: [text](url1, [url2) → [text](url1) [label](url2)
  // This fixes LLM output like:
  // [analógia k § 2 písm. x) a y)](4558028#..., [4558028#...)
  // IMPORTANT: Must run BEFORE Step 4 (orphan bracket cleanup) to preserve the [url2 bracket
  content = content.replace(
    /\[([^\]]+)\]\(([^,\[\]]+),\s*\[([^\]]+)\)/g,
    (_match, text, url1, url2) => {
      const escapedUrl1 = url1.trim().replace(/(?<!\\)_/g, '\\_');
      const escapedUrl2 = url2.trim().replace(/(?<!\\)_/g, '\\_');
      return `[${text}](${escapedUrl1}) [${extractLabelFromUrl(url2.trim())}](${escapedUrl2})`;
    }
  );

  // Step 3.6b: Fix short-code labels in properly decoded links
  // When backend decode_text decoded the URI but LLM used short code as anchor text:
  // [=2#1](https://full-url) → [extractedLabel](https://full-url)
  // This is a frontend safety net; the backend replace_short_code_labels should catch most cases.
  content = content.replace(
    /\[=\d+(?:#[^\]]+)?\]\(([^)]+)\)/g,
    (_match, url) => {
      // If href is still a short code, let step 3.7 handle it
      if (/^=\d+/.test(url)) return _match;
      return `[${extractLabelFromUrl(url)}](${url})`;
    }
  );

  // Step 3.7: Strip leaked short codes from backend URI shortener
  // Short codes (=digits or =digits#fragment) should NEVER reach the frontend.
  // If they do (backend decode_text failure), remove the broken link and keep just the text.
  // When the anchor text is just 🔗, drop entirely to avoid orphaned emoji.
  content = content.replace(
    /\[([^\]]+)\]\(=\d+(?:#[^)]+)?\)/g,
    (_match, text) => (text.trim() === '🔗' ? '' : text)
  );
  content = content.replace(/\[=\d+(?:#[^\]]+)?\](?!\()/g, '');

  // Step 3.8: Strip bare section references with no URL
  // LLM outputs [§ 5#37] or [§ 3#19] as in-document section pointers without a link target.
  // These become broken anchors in the markdown parser. Remove them entirely.
  content = content.replace(/\[§\s*\d+#[^\]]+\](?!\()/g, '');

  // Step 4: Clean up any remaining malformed brackets
  // Remove single opening or closing brackets that don't form valid markdown
  // IMPORTANT: Don't remove ] that closes a valid [text] pair
  // The regex only removes:
  // - ] that is not part of a [text] or [text](url) pattern
  // - [ that is not followed by text and closing ]
  // Fixed: Previous regex incorrectly removed ] from [text] patterns like [Finančný Kompas]
  content = content.replace(
    // Match truly orphan [ not followed by any ] before next [ or end
    /\[(?![^\[\]]*\])/g,
    ''
  );
  content = content.replace(
    // Match truly orphan ] not preceded by any [ after previous ] or start
    /(?<!\[[^\[\]]*)\]/g,
    ''
  );

  // Step 4.5: Remove orphaned 🔗 emoji not inside markdown link syntax.
  // These appear when hallucination repair strips [🔗](ref) → 🔗,
  // or when the LLM produces bare 🔗 without link syntax.
  // Only match 🔗 NOT followed by optional spaces + ] (inside [🔗](url) the ] always follows).
  content = content.replace(/🔗(?!\s*\])/g, '');

  // Step 5: Fix unclosed markdown links with numeric ID + fragment
  // Pattern: [text](12345#section.name without closing )
  // This fixes malformed links from LLM output like: [ 🔗 ](4551492#predpis.clanok-1...
  // Matches when URL is followed by: end of line, period+space, or other sentence-ending punctuation
  content = content.replace(
    /\[([^\]]*)\]\((\d+#[a-zA-Z0-9._-]+)(?=\.\s|$)/gm,
    (_match, text, url) => {
      // Escape underscores in URL to prevent markdown emphasis interpretation
      // Use negative lookbehind to avoid double-escaping already-escaped underscores
      const escapedUrl = url.replace(/(?<!\\)_/g, '\\_');
      return `[${text}](${escapedUrl})`;
    }
  );

  // Step 6: Strip SIGNIFICANCE blocks first (handles both SIGNIFICANCE--> and SIGNIFICANCE-- variants).
  // LLMs sometimes omit the closing > producing SIGNIFICANCE-- instead of SIGNIFICANCE-->.
  // LLMs also sometimes omit the opening < producing !--SIGNIFICANCE instead of <!--SIGNIFICANCE.
  // The optional <? handles both cases. Must run before generic HTML comment stripping.
  content = content.replace(
    /<?\!--SIGNIFICANCE[\s\S]*?SIGNIFICANCE--[>]?/g,
    ''
  );

  // Step 6: Strip all remaining HTML comments (e.g. <!--...--> blocks other than SIGNIFICANCE)
  // Safety net: ensures copied text never includes backend HTML comments
  content = content.replace(/<!--[\s\S]*?-->/g, '');

  // Step 6b: Strip incomplete SIGNIFICANCE block during streaming
  // (closing tag hasn't arrived yet, so neither step above will match)
  // Handles both <!--SIGNIFICANCE and !--SIGNIFICANCE (missing <)
  content = content.replace(/<?\!--SIGNIFICANCE[\s\S]*$/g, '');

  return content;
}
